import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, Trash2, Save, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '@/api/axios';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [newProject, setNewProject] = useState('');
  const [newProjectGroup, setNewProjectGroup] = useState('');
  const [newUser, setNewUser] = useState('');
  const [loading, setLoading] = useState(false);

  // Inline-edit state for project master
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [editingProjectGroup, setEditingProjectGroup] = useState('');

  // Project Schedule state
  const [scheduleProject, setScheduleProject] = useState('');
  const [scheduleEntries, setScheduleEntries] = useState([
    { day: 1, enabled: false, user: '' },
    { day: 2, enabled: false, user: '' },
    { day: 3, enabled: false, user: '' },
    { day: 4, enabled: false, user: '' },
    { day: 5, enabled: false, user: '' },
  ]);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Specialty Master state
  const [specialties, setSpecialties] = useState([]);
  const [newSpecialty, setNewSpecialty] = useState('');
  const [editingSpecialtyId, setEditingSpecialtyId] = useState(null);
  const [editingSpecialtyName, setEditingSpecialtyName] = useState('');

  // Inline-edit specialty selection for project master edit row
  const [editingProjectSpecialty, setEditingProjectSpecialty] = useState('');
  const [newProjectSpecialty, setNewProjectSpecialty] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, usersRes, specialtiesRes] = await Promise.all([
        axiosInstance.get('/masters/projects'),
        axiosInstance.get('/masters/users'),
        axiosInstance.get('/masters/specialties'),
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
      setSpecialties(specialtiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!newProject.trim()) return;

    setLoading(true);
    try {
      await axiosInstance.post('/masters/projects', {
        project_name: newProject.trim(),
        group_name: newProjectGroup.trim() || null,
        specialty: newProjectSpecialty.trim() || null,
      });
      toast.success('Project added successfully');
      setNewProject('');
      setNewProjectGroup('');
      setNewProjectSpecialty('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add project');
    } finally {
      setLoading(false);
    }
  };

  const startEditProject = (project) => {
    setEditingProjectId(project.id);
    setEditingProjectName(project.project_name);
    setEditingProjectGroup(project.group_name || '');
    setEditingProjectSpecialty(project.specialty || '');
  };

  const cancelEditProject = () => {
    setEditingProjectId(null);
    setEditingProjectName('');
    setEditingProjectGroup('');
    setEditingProjectSpecialty('');
  };

  const saveEditProject = async (project) => {
    if (!editingProjectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    try {
      await axiosInstance.put(`/masters/projects/${project.id}`, {
        project_name: editingProjectName.trim(),
        group_name: editingProjectGroup.trim() || null,
        specialty: editingProjectSpecialty.trim() || null,
      });
      toast.success('Project updated');
      cancelEditProject();
      fetchData();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const toggleProjectActive = async (project) => {
    try {
      await axiosInstance.put(`/masters/projects/${project.id}`, {
        active: !project.active,
      });
      toast.success(project.active ? 'Project marked inactive' : 'Project marked active');
      fetchData();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await axiosInstance.delete(`/masters/projects/${id}`);
      toast.success('Project deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.trim()) return;

    setLoading(true);
    try {
      await axiosInstance.post('/masters/users', { user_name: newUser });
      toast.success('User added successfully');
      setNewUser('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axiosInstance.delete(`/masters/users/${id}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const toggleUserActive = async (user) => {
    try {
      await axiosInstance.put(`/masters/users/${user.id}`, {
        active: !user.active,
      });
      toast.success(user.active ? 'User marked inactive' : 'User marked active');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  // ---- Specialty Master handlers ----
  const handleAddSpecialty = async (e) => {
    e.preventDefault();
    if (!newSpecialty.trim()) return;
    setLoading(true);
    try {
      await axiosInstance.post('/masters/specialties', {
        specialty: newSpecialty.trim(),
      });
      toast.success('Specialty added');
      setNewSpecialty('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add specialty');
    } finally {
      setLoading(false);
    }
  };

  const startEditSpecialty = (spec) => {
    setEditingSpecialtyId(spec.id);
    setEditingSpecialtyName(spec.specialty);
  };

  const cancelEditSpecialty = () => {
    setEditingSpecialtyId(null);
    setEditingSpecialtyName('');
  };

  const saveEditSpecialty = async (spec) => {
    if (!editingSpecialtyName.trim()) {
      toast.error('Specialty is required');
      return;
    }
    try {
      await axiosInstance.put(`/masters/specialties/${spec.id}`, {
        specialty: editingSpecialtyName.trim(),
      });
      toast.success('Specialty updated (mapped projects updated too)');
      cancelEditSpecialty();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update specialty');
    }
  };

  const handleDeleteSpecialty = async (spec) => {
    if (
      !window.confirm(
        `Delete specialty "${spec.specialty}"?\nProjects currently mapped to it will be cleared.`
      )
    )
      return;
    try {
      await axiosInstance.delete(`/masters/specialties/${spec.id}`);
      toast.success('Specialty deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete specialty');
    }
  };

  // ---- Project Schedule handlers ----
  const dayLabels = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday' };

  const loadProjectSchedule = async (projectName) => {
    setScheduleProject(projectName);
    if (!projectName) {
      setScheduleEntries([
        { day: 1, enabled: false, user: '' },
        { day: 2, enabled: false, user: '' },
        { day: 3, enabled: false, user: '' },
        { day: 4, enabled: false, user: '' },
        { day: 5, enabled: false, user: '' },
      ]);
      return;
    }
    setScheduleLoading(true);
    try {
      const res = await axiosInstance.get(
        `/masters/project-schedule/${encodeURIComponent(projectName)}`
      );
      const entries = res.data.entries.map((e) => ({
        day: e.day,
        enabled: e.enabled,
        user: e.user || '',
      }));
      setScheduleEntries(entries);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const updateScheduleEntry = (day, patch) => {
    setScheduleEntries((prev) =>
      prev.map((e) => (e.day === day ? { ...e, ...patch } : e))
    );
  };

  const saveProjectSchedule = async () => {
    if (!scheduleProject) {
      toast.error('Select a project first');
      return;
    }
    // Client-side validation: every enabled day needs a user
    for (const e of scheduleEntries) {
      if (e.enabled && !e.user.trim()) {
        toast.error(`Please select a user for ${dayLabels[e.day]}`);
        return;
      }
    }
    setScheduleLoading(true);
    try {
      const payload = {
        project_name: scheduleProject,
        entries: scheduleEntries.map((e) => ({
          day: e.day,
          enabled: e.enabled,
          user: e.enabled ? e.user.trim() : null,
        })),
      };
      await axiosInstance.put(
        `/masters/project-schedule/${encodeURIComponent(scheduleProject)}`,
        payload
      );
      toast.success('Schedule saved');
    } catch (error) {
      toast.error(error.response?.data?.detail?.[0]?.msg || 'Failed to save schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-medium tracking-tight">Admin Panel</h1>
            <p className="text-xs text-muted-foreground mt-1">Manage master data</p>
          </div>
          <Button
            data-testid="back-to-dashboard-button"
            variant="ghost"
            className="rounded-sm"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="projects" className="space-y-6">
          <TabsList className="bg-white border border-border rounded-sm p-1">
            <TabsTrigger value="projects" className="rounded-sm" data-testid="projects-tab">
              Project Master
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-sm" data-testid="users-tab">
              User Master
            </TabsTrigger>
            <TabsTrigger value="specialties" className="rounded-sm" data-testid="specialties-tab">
              Specialty Master
            </TabsTrigger>
            <TabsTrigger value="project-schedule" className="rounded-sm" data-testid="project-schedule-tab">
              Project Schedule
            </TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Add New Project</h2>
              <form onSubmit={handleAddProject} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="new-project" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Project Name
                  </Label>
                  <Input
                    data-testid="new-project-input"
                    id="new-project"
                    placeholder="Enter project name"
                    value={newProject}
                    onChange={(e) => setNewProject(e.target.value)}
                    className="rounded-sm mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="new-project-group" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Group Name (optional)
                  </Label>
                  <Input
                    data-testid="new-project-group-input"
                    id="new-project-group"
                    placeholder="e.g. Group A"
                    value={newProjectGroup}
                    onChange={(e) => setNewProjectGroup(e.target.value)}
                    className="rounded-sm mt-2"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Specialty (optional)
                  </Label>
                  <Select
                    value={newProjectSpecialty || 'none'}
                    onValueChange={(v) => setNewProjectSpecialty(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger data-testid="new-project-specialty-select" className="rounded-sm mt-2">
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {[...specialties]
                        .sort((a, b) => a.specialty.localeCompare(b.specialty))
                        .map((s) => (
                          <SelectItem key={s.id} value={s.specialty}>
                            {s.specialty}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    data-testid="add-project-button"
                    type="submit"
                    className="rounded-sm bg-primary hover:bg-primary/90 h-10 w-full"
                    disabled={loading}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Project
                  </Button>
                </div>
              </form>
            </div>

            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Existing Projects</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Project Name
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Group Name
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Specialty
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Status
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Created At
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No projects found. Add one above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      projects.map((project) => {
                        const isEditing = editingProjectId === project.id;
                        return (
                          <TableRow
                            key={project.id}
                            className={`border-b hover:bg-muted/50 ${
                              project.active === false ? 'opacity-60' : ''
                            }`}
                            data-testid="project-row"
                          >
                            <TableCell className="px-2 py-3 text-sm">
                              {isEditing ? (
                                <Input
                                  data-testid="edit-project-name-input"
                                  value={editingProjectName}
                                  onChange={(e) => setEditingProjectName(e.target.value)}
                                  className="rounded-sm h-8"
                                />
                              ) : (
                                project.project_name
                              )}
                            </TableCell>
                            <TableCell className="px-2 py-3 text-sm">
                              {isEditing ? (
                                <Input
                                  data-testid="edit-project-group-input"
                                  value={editingProjectGroup}
                                  onChange={(e) => setEditingProjectGroup(e.target.value)}
                                  placeholder="Group name"
                                  className="rounded-sm h-8"
                                />
                              ) : (
                                project.group_name || <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="px-2 py-3 text-sm">
                              {isEditing ? (
                                <Select
                                  value={editingProjectSpecialty || 'none'}
                                  onValueChange={(v) =>
                                    setEditingProjectSpecialty(v === 'none' ? '' : v)
                                  }
                                >
                                  <SelectTrigger
                                    data-testid="edit-project-specialty-select"
                                    className="rounded-sm h-8"
                                  >
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">— None —</SelectItem>
                                    {[...specialties]
                                      .sort((a, b) => a.specialty.localeCompare(b.specialty))
                                      .map((s) => (
                                        <SelectItem key={s.id} value={s.specialty}>
                                          {s.specialty}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                project.specialty || <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="px-2 py-3 text-sm">
                              <span
                                data-testid="project-status-badge"
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  project.active === false
                                    ? 'bg-muted text-muted-foreground'
                                    : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {project.active === false ? 'Inactive' : 'Active'}
                              </span>
                            </TableCell>
                            <TableCell className="px-2 py-3 text-sm">
                              {new Date(project.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="px-2 py-3">
                              {isEditing ? (
                                <div className="flex gap-1">
                                  <Button
                                    data-testid="save-project-button"
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-sm text-primary hover:bg-primary/10"
                                    onClick={() => saveEditProject(project)}
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    data-testid="cancel-edit-project-button"
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-sm"
                                    onClick={cancelEditProject}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <Button
                                    data-testid="toggle-project-active-button"
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-sm"
                                    onClick={() => toggleProjectActive(project)}
                                    title={project.active === false ? 'Mark Active' : 'Mark Inactive'}
                                  >
                                    {project.active === false ? 'Activate' : 'Deactivate'}
                                  </Button>
                                  <Button
                                    data-testid="edit-project-button"
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-sm"
                                    onClick={() => startEditProject(project)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    data-testid="delete-project-button"
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDeleteProject(project.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Add New User</h2>
              <form onSubmit={handleAddUser} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="new-user" className="sr-only">
                    User Name
                  </Label>
                  <Input
                    data-testid="new-user-input"
                    id="new-user"
                    placeholder="Enter user name"
                    value={newUser}
                    onChange={(e) => setNewUser(e.target.value)}
                    className="rounded-sm"
                    required
                  />
                </div>
                <Button
                  data-testid="add-user-button"
                  type="submit"
                  className="rounded-sm bg-primary hover:bg-primary/90"
                  disabled={loading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </form>
            </div>

            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Existing Users</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        User Name
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Status
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Created At
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No users found. Add one above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow
                          key={user.id}
                          className={`border-b hover:bg-muted/50 ${
                            user.active === false ? 'opacity-60' : ''
                          }`}
                          data-testid="user-row"
                        >
                          <TableCell className="px-2 py-3 text-sm">{user.user_name}</TableCell>
                          <TableCell className="px-2 py-3 text-sm">
                            <span
                              data-testid="user-status-badge"
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                user.active === false
                                  ? 'bg-muted text-muted-foreground'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {user.active === false ? 'Inactive' : 'Active'}
                            </span>
                          </TableCell>
                          <TableCell className="px-2 py-3 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="px-2 py-3">
                            <div className="flex gap-1">
                              <Button
                                data-testid="toggle-user-active-button"
                                variant="ghost"
                                size="sm"
                                className="rounded-sm"
                                onClick={() => toggleUserActive(user)}
                                title={user.active === false ? 'Mark Active' : 'Mark Inactive'}
                              >
                                {user.active === false ? 'Activate' : 'Deactivate'}
                              </Button>
                              <Button
                                data-testid="delete-user-button"
                                variant="ghost"
                                size="sm"
                                className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Specialty Master Tab */}
          <TabsContent value="specialties" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Add New Specialty</h2>
              <form onSubmit={handleAddSpecialty} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="new-specialty" className="sr-only">
                    Specialty
                  </Label>
                  <Input
                    data-testid="new-specialty-input"
                    id="new-specialty"
                    placeholder="Enter specialty (e.g. Cardiology)"
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    className="rounded-sm"
                    required
                  />
                </div>
                <Button
                  data-testid="add-specialty-button"
                  type="submit"
                  className="rounded-sm bg-primary hover:bg-primary/90"
                  disabled={loading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Specialty
                </Button>
              </form>
            </div>

            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Existing Specialties</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Renaming a specialty automatically updates all projects mapped to it.
              </p>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Specialty
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Mapped Projects
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Created At
                      </TableHead>
                      <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {specialties.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No specialties yet. Add one above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      [...specialties]
                        .sort((a, b) => a.specialty.localeCompare(b.specialty))
                        .map((spec) => {
                          const isEditing = editingSpecialtyId === spec.id;
                          const mappedCount = projects.filter(
                            (p) => p.specialty === spec.specialty
                          ).length;
                          return (
                            <TableRow
                              key={spec.id}
                              className="border-b hover:bg-muted/50"
                              data-testid="specialty-row"
                            >
                              <TableCell className="px-2 py-3 text-sm">
                                {isEditing ? (
                                  <Input
                                    data-testid="edit-specialty-input"
                                    value={editingSpecialtyName}
                                    onChange={(e) => setEditingSpecialtyName(e.target.value)}
                                    className="rounded-sm h-8"
                                  />
                                ) : (
                                  spec.specialty
                                )}
                              </TableCell>
                              <TableCell className="px-2 py-3 text-sm">
                                {mappedCount} project{mappedCount === 1 ? '' : 's'}
                              </TableCell>
                              <TableCell className="px-2 py-3 text-sm">
                                {new Date(spec.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="px-2 py-3">
                                {isEditing ? (
                                  <div className="flex gap-1">
                                    <Button
                                      data-testid="save-specialty-button"
                                      variant="ghost"
                                      size="sm"
                                      className="rounded-sm text-primary hover:bg-primary/10"
                                      onClick={() => saveEditSpecialty(spec)}
                                    >
                                      <Save className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      data-testid="cancel-edit-specialty-button"
                                      variant="ghost"
                                      size="sm"
                                      className="rounded-sm"
                                      onClick={cancelEditSpecialty}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex gap-1">
                                    <Button
                                      data-testid="edit-specialty-button"
                                      variant="ghost"
                                      size="sm"
                                      className="rounded-sm"
                                      onClick={() => startEditSpecialty(spec)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      data-testid="delete-specialty-button"
                                      variant="ghost"
                                      size="sm"
                                      className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => handleDeleteSpecialty(spec)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Project Schedule Tab */}
          <TabsContent value="project-schedule" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Project Weekly Schedule</h2>
              <div className="max-w-md space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Project
                </Label>
                <Select
                  value={scheduleProject}
                  onValueChange={loadProjectSchedule}
                >
                  <SelectTrigger data-testid="schedule-project-select" className="rounded-sm">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...projects]
                      .sort((a, b) => a.project_name.localeCompare(b.project_name))
                      .map((p) => (
                        <SelectItem key={p.id} value={p.project_name}>
                          {p.project_name}
                          {p.group_name ? ` — ${p.group_name}` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {scheduleProject && (
              <div className="bg-white border border-border shadow-sm rounded-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-medium">Schedule for: {scheduleProject}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Check the days this project runs, then assign a user. Enabled days require a user.
                    </p>
                  </div>
                  <Button
                    data-testid="save-schedule-button"
                    onClick={saveProjectSchedule}
                    disabled={scheduleLoading}
                    className="rounded-sm bg-primary hover:bg-primary/90"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {scheduleLoading ? 'Saving...' : 'Save Schedule'}
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs w-24">
                          Active
                        </TableHead>
                        <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                          Weekday
                        </TableHead>
                        <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
                          User
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduleEntries.map((entry) => (
                        <TableRow
                          key={entry.day}
                          className="border-b hover:bg-muted/50"
                          data-testid={`schedule-row-${entry.day}`}
                        >
                          <TableCell className="px-2 py-3">
                            <Checkbox
                              data-testid={`schedule-checkbox-${entry.day}`}
                              checked={entry.enabled}
                              onCheckedChange={(checked) =>
                                updateScheduleEntry(entry.day, {
                                  enabled: Boolean(checked),
                                  // Clear user when disabled
                                  user: checked ? entry.user : '',
                                })
                              }
                              className="rounded-sm"
                            />
                          </TableCell>
                          <TableCell className="px-2 py-3 text-sm font-medium">
                            {dayLabels[entry.day]}
                          </TableCell>
                          <TableCell className="px-2 py-3">
                            <Select
                              value={entry.user}
                              onValueChange={(v) =>
                                updateScheduleEntry(entry.day, { user: v })
                              }
                              disabled={!entry.enabled}
                            >
                              <SelectTrigger
                                data-testid={`schedule-user-select-${entry.day}`}
                                className="rounded-sm max-w-xs"
                              >
                                <SelectValue
                                  placeholder={
                                    entry.enabled ? 'Select user *' : 'Select user'
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {[...users]
                                  .sort((a, b) => a.user_name.localeCompare(b.user_name))
                                  .map((u) => (
                                    <SelectItem key={u.id} value={u.user_name}>
                                      {u.user_name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            {entry.enabled && !entry.user && (
                              <p className="text-xs text-destructive mt-1">
                                User required for enabled day
                              </p>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
